import type {
  ApiErrorBody, AuthMethods, Billing, BillingCurrency, Branding, Checkout, PriceKey, PublicPricing, SignupCountry, SignupPlan, SignupStatus, UpgradeRequired, ForkResult, LegacyPublicShare, McpConnection, Me, MemberAdd, MemberRole, PlanCreate, PlanDetail, PlanEnvelope,
  CheckInBody, CheckInSaved, PlanMark, PlanMarks, PlanMember, PlanNotice, PlanNotices, PlanPeople, PlanSummary, PlanWrite, PublicShare, Settings, Share, ShareCreate, ShareDraft, ShareDraftDetail, VersionSummary, Waypoint, WireBalances, WirePlan,
} from './types.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: ApiErrorBody | null;

  constructor(status: number, body: ApiErrorBody | null) {
    super(body?.message ?? body?.error ?? `HTTP ${status}`);
    this.status = status;
    this.code = body?.error ?? (status === 404 ? 'not_found' : 'http_error');
    this.body = body;
  }

  /** Issue messages the server attached to a field, e.g. `/password` on a 422. */
  issueFor(field: string): string | null {
    for (const issue of this.body?.issues ?? []) {
      const { path, message } = issue as { path?: unknown; message?: unknown };
      if (typeof path === 'string' && path.replace(/^\//, '') === field && typeof message === 'string') return message;
    }
    return null;
  }

  get isStale() {
    return this.status === 409 && (this.code === 'stale' || this.body?.plan !== undefined);
  }

  get isPlanTooLarge() {
    return this.code === 'plan_too_large';
  }

  /** What a 402 `upgrade_required` asks for; null for every other error. */
  get upgrade(): UpgradeRequired | null {
    const body = this.body;
    if (this.status !== 402 || this.code !== 'upgrade_required' || !body?.feature) return null;
    return { feature: body.feature, limit: body.limit ?? null, tier: body.tier ?? 'none', upgradeUrl: body.upgradeUrl ?? '' };
  }
}

/** The upgrade a failed write asks for, if that is why it failed. */
export const upgradeOf = (error: unknown): UpgradeRequired | null => (error instanceof ApiError ? error.upgrade : null);

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  // The Worker's CSRF check wants JSON on every write, bodyless ones included.
  const headers: Record<string, string> = { accept: 'application/json' };
  if (method !== 'GET') headers['content-type'] = 'application/json';
  const init: RequestInit = { method, credentials: 'same-origin', headers };
  if (body !== undefined) init.body = JSON.stringify(body);
  const response = await fetch(`/api${path}`, init);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const json: unknown = text ? safeJson(text) : null;
  if (!response.ok) throw new ApiError(response.status, (json as ApiErrorBody | null) ?? null);
  return json as T;
}

/** Servers that predate kinds answer with a bare showcase. */
function normalizePublicShare(body: PublicShare | LegacyPublicShare): PublicShare {
  if ('schemaVersion' in body && 'kind' in body) return body;
  const legacy = body as LegacyPublicShare;
  return {
    schemaVersion: 2, kind: 'showcase', title: legacy.title, ownerFirstName: legacy.ownerFirstName, createdAt: legacy.createdAt,
    expiresAt: null, canFork: false, data: { showAmounts: legacy.showAmounts, result: legacy.result },
  };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const planPath = (planId: string) => `/plans/${encodeURIComponent(planId)}`;
const versionsPath = (planId: string | null) => (planId ? `${planPath(planId)}/versions` : '/plan/versions');
const waypointsPath = (planId: string | null) => (planId ? `${planPath(planId)}/waypoints` : '/waypoints');
const noticesPath = (planId: string | null) => (planId ? `${planPath(planId)}/notices` : '/notices');
const marksPath = (planId: string | null) => (planId ? `${planPath(planId)}/marks` : '/marks');

export const api = {
  me: () => request<Me>('GET', '/me'),
  deleteMe: () => request<void>('DELETE', '/me'),
  logout: () => request<void>('POST', '/auth/logout'),
  authMethods: () => request<AuthMethods>('GET', '/auth/methods'),
  devLogin: (email: string) => request<unknown>('POST', '/auth/dev-login', { email }),
  loginWithPassword: (email: string, password: string) => request<unknown>('POST', '/auth/password/login', { email, password }),
  registerWithPassword: (email: string, password: string) => request<unknown>('POST', '/auth/password/register', { email, password }),
  appleStartUrl: (returnTo: string) => `/api/auth/apple/start?return_to=${encodeURIComponent(returnTo)}`,

  /** Plan-scoped calls take the plan id; null means the legacy routes (the caller's primary plan). */
  getPlan: () => request<PlanEnvelope>('GET', '/plan'),
  putPlan: (write: PlanWrite, planId: string | null = null) =>
    request<{ version: number; updatedAt: string }>('PUT', planId ? planPath(planId) : '/plan', write),
  versions: (planId: string | null = null, limit = 50) =>
    request<{ versions: VersionSummary[] }>('GET', `${versionsPath(planId)}?limit=${limit}`),
  version: (version: number, planId: string | null = null) =>
    request<{ plan: WirePlan; version: number; source: string; summary: string; createdAt: string }>('GET', `${versionsPath(planId)}/${version}`),
  restore: (version: number, baseVersion: number, planId: string | null = null) =>
    request<{ version: number; updatedAt: string }>('POST', `${versionsPath(planId)}/${version}/restore`, { baseVersion }),

  waypoints: (planId: string | null = null) => request<{ waypoints: Waypoint[] }>('GET', waypointsPath(planId)),
  addWaypoint: (date: string, balances: WireBalances, planId: string | null = null) =>
    request<{ waypoint: Waypoint }>('POST', waypointsPath(planId), { date, balances }),
  checkIn: (body: CheckInBody, planId: string | null = null) => request<CheckInSaved>('POST', waypointsPath(planId), body),

  notices: (planId: string | null = null) => request<PlanNotices>('GET', noticesPath(planId)),
  dismissNotice: (notice: PlanNotice, planId: string | null = null) =>
    request<PlanNotices>('PUT', `${noticesPath(planId)}/${encodeURIComponent(notice)}`),

  marks: (planId: string | null = null) => request<PlanMarks>('GET', marksPath(planId)),
  putMarks: (marks: readonly PlanMark[], planId: string | null = null) => request<PlanMarks>('PUT', marksPath(planId), { marks }),

  plans: () => request<{ plans: PlanSummary[] }>('GET', '/plans'),
  plan: (planId: string) => request<PlanDetail>('GET', planPath(planId)),
  createPlan: (create: PlanCreate) => request<PlanSummary>('POST', '/plans', create),
  renamePlan: (planId: string, name: string) => request<PlanSummary>('PATCH', planPath(planId), { name }),
  deletePlan: (planId: string) => request<void>('DELETE', planPath(planId)),

  people: (planId: string) => request<PlanPeople>('GET', `${planPath(planId)}/members`),
  addMember: (planId: string, add: MemberAdd) => request<{ member: PlanMember }>('POST', `${planPath(planId)}/members`, add),
  updateMember: (planId: string, userId: string, patch: { role?: MemberRole; canRunScenarios?: boolean }) =>
    request<{ member: PlanMember }>('PATCH', `${planPath(planId)}/members/${encodeURIComponent(userId)}`, patch),
  removeMember: (planId: string, userId: string) => request<void>('DELETE', `${planPath(planId)}/members/${encodeURIComponent(userId)}`),
  leavePlan: (planId: string) => request<void>('DELETE', `${planPath(planId)}/members/me`),

  shareDrafts: () => request<{ drafts: ShareDraft[] }>('GET', '/share-drafts'),
  shareDraft: (id: string) => request<ShareDraftDetail>('GET', `/share-drafts/${encodeURIComponent(id)}`),
  confirmShareDraft: (id: string) => request<{ share: Share; url: string }>('POST', `/share-drafts/${encodeURIComponent(id)}/confirm`, {}),
  discardShareDraft: (id: string) => request<void>('POST', `/share-drafts/${encodeURIComponent(id)}/discard`, {}),

  shares: () => request<{ shares: Share[] }>('GET', '/shares'),
  createShare: (share: ShareCreate) => request<{ share: Share; url: string }>('POST', '/shares', share),
  revokeShare: (token: string) => request<void>('DELETE', `/shares/${encodeURIComponent(token)}`),
  publicShare: async (token: string) =>
    normalizePublicShare(await request<PublicShare | LegacyPublicShare>('GET', `/public/shares/${encodeURIComponent(token)}`)),
  forkShare: (token: string, name?: string) =>
    request<ForkResult>('POST', `/public/shares/${encodeURIComponent(token)}/fork`, name ? { name } : {}),
  joinWaitlist: (email: string, source?: string) => request<{ ok: true }>('POST', '/waitlist', { email, ...(source ? { source } : {}) }),

  settings: () => request<Settings>('GET', '/settings'),
  putSettings: (settings: Settings) => request<Settings>('PUT', '/settings', settings),

  billing: (currency?: BillingCurrency) => request<Billing>('GET', `/billing${currency ? `?currency=${currency}` : ''}`),
  checkout: (plan: PriceKey, currency?: BillingCurrency) => request<Checkout>('POST', '/billing/checkout', { plan, ...(currency ? { currency } : {}) }),
  billingPortal: () => request<{ url: string }>('POST', '/billing/portal', {}),
  putBranding: (branding: Branding) => request<{ branding: Branding }>('PUT', '/billing/branding', branding),
  deleteBranding: () => request<void>('DELETE', '/billing/branding'),
  pricing: (country?: SignupCountry | null) => request<PublicPricing>('GET', `/billing/pricing${country ? `?country=${country}` : ''}`),
  signupCheckout: (plan: SignupPlan, currency: BillingCurrency, country?: SignupCountry | null) =>
    request<{ url: string }>('POST', '/billing/signup-checkout', { plan, currency, ...(country ? { country } : {}) }),
  signupStatus: (sessionId: string) => request<SignupStatus>('GET', `/billing/signup-status?session_id=${encodeURIComponent(sessionId)}`),
  claimPassword: (sessionId: string, password: string) => request<unknown>('POST', '/auth/password/claim', { session_id: sessionId, password }),

  mcpConnections: () => request<{ connections: McpConnection[] }>('GET', '/mcp/connections'),
  disconnectMcp: (id: string) => request<void>('DELETE', `/mcp/connections/${encodeURIComponent(id)}`),
};
